/**
 * SellerStatsPage - Statistiche unificate per Seller
 * Include: Analytics avanzate, Report ordini, Trend vendite, Top clienti/prodotti
 */
import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useI18n } from '../contexts/I18nContext';
import { formatCurrency, formatNumber, getErrorMessage } from '@/lib/utils';
import axios from 'axios';
import { toast } from 'sonner';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  AreaChart,
  Area
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Skeleton } from '../components/ui/skeleton';
import {
  TrendingUp,
  TrendingDown,
  Download,
  ShoppingCart,
  DollarSign,
  Package,
  Users,
  Calendar,
  BarChart3,
  Wallet,
  History,
  Building2,
  CreditCard,
  ArrowUpRight,
  ArrowDownRight,
  Loader2,
  FileSpreadsheet,
  PieChart as PieChartIcon
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Colori per i grafici
const COLORS = ['#16a34a', '#2563eb', '#7c3aed', '#db2777', '#ea580c', '#dc2626', '#0891b2', '#4f46e5'];
const STATUS_COLORS: Record<string, string> = {
  delivered: '#16a34a',
  shipped: '#2563eb',
  ready: '#7c3aed',
  in_preparation: '#f59e0b',
  confirmed: '#0891b2',
  submitted: '#6366f1',
  draft: '#9ca3af',
  cancelled: '#ef4444'
};

interface SalesTrendData {
  date: string;
  revenue: number;
  orders: number;
}

interface TopProduct {
  product_id: string;
  name: string;
  quantity: number;
  revenue: number;
  orders_count: number;
}

interface TopCustomer {
  customer_id: string;
  name: string;
  orders_count: number;
  total_spent: number;
  avg_order_value: number;
}

const SellerStatsPage: React.FC = () => {
  const { user } = useAuth();
  const { t } = useI18n();
  
  const [activeTab, setActiveTab] = useState('panoramica');
  const [period, setPeriod] = useState('30');
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState<string | null>(null);
  
  // Dati statistiche
  const [summary, setSummary] = useState<any>(null);
  const [salesTrend, setSalesTrend] = useState<SalesTrendData[]>([]);
  const [topProducts, setTopProducts] = useState<TopProduct[]>([]);
  const [topCustomers, setTopCustomers] = useState<TopCustomer[]>([]);
  const [ordersByStatus, setOrdersByStatus] = useState<any[]>([]);
  const [monthlyRevenue, setMonthlyRevenue] = useState<any[]>([]);

  const fetchStats = useCallback(async () => {
    setLoading(true);
    try {
      const [trendRes, productsRes, customersRes, statusRes, monthlyRes] = await Promise.all([
        axios.get(`${API}/analytics/sales-trend?days=${period}`).catch(() => ({ data: { data: [], summary: {} } })),
        axios.get(`${API}/analytics/top-products?limit=10&days=${period}`).catch(() => ({ data: [] })),
        axios.get(`${API}/analytics/top-customers?limit=10&days=${period}`).catch(() => ({ data: [] })),
        axios.get(`${API}/analytics/orders-by-status`).catch(() => ({ data: [] })),
        axios.get(`${API}/analytics/monthly-revenue?months=6`).catch(() => ({ data: [] }))
      ]);

      setSalesTrend(Array.isArray(trendRes.data.data) ? trendRes.data.data : []);
      setSummary(trendRes.data.summary || {});
      setTopProducts(Array.isArray(productsRes.data) ? productsRes.data : []);
      setTopCustomers(Array.isArray(customersRes.data) ? customersRes.data : []);
      setOrdersByStatus(Array.isArray(statusRes.data) ? statusRes.data : []);
      setMonthlyRevenue(Array.isArray(monthlyRes.data) ? monthlyRes.data : []);
      
    } catch (error) {
      console.error('Error fetching stats:', error);
      toast.error('Errore nel caricamento delle statistiche');
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const handleExport = async (format: 'csv' | 'xlsx' | 'pdf') => {
    setExporting(format);
    try {
      const res = await axios.get(`${API}/analytics/export/${format}?days=${period}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `statistiche_${new Date().toISOString().slice(0,10)}.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Export completato');
    } catch (error) {
      toast.error('Errore nell\'export');
    } finally {
      setExporting(null);
    }
  };

  const getStatusLabel = (status: string) => {
    const labels: Record<string, string> = {
      draft: 'Bozze',
      submitted: 'Inviati',
      confirmed: 'Confermati',
      in_preparation: 'In Preparazione',
      ready: 'Pronti',
      shipped: 'Spediti',
      delivered: 'Consegnati',
      completed: 'Completati',
      cancelled: 'Annullati'
    };
    return labels[status] || status;
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('it-IT', {
      day: '2-digit',
      month: 'short'
    });
  };

  if (loading) {
    return (
      <div className="space-y-6 p-6">
        <Skeleton className="h-10 w-64" />
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[1,2,3,4].map(i => <Skeleton key={i} className="h-32" />)}
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="seller-stats-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-heading font-bold">Statistiche</h1>
          <p className="text-muted-foreground mt-1">
            Analisi vendite, prodotti e clienti
          </p>
        </div>
        <div className="flex gap-2">
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-40">
              <Calendar className="mr-2 h-4 w-4" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Ultima Settimana</SelectItem>
              <SelectItem value="30">Ultimo Mese</SelectItem>
              <SelectItem value="90">Ultimi 3 Mesi</SelectItem>
              <SelectItem value="365">Ultimo Anno</SelectItem>
            </SelectContent>
          </Select>
          <Button 
            variant="outline" 
            onClick={() => handleExport('csv')} 
            disabled={!!exporting}
          >
            {exporting === 'csv' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
            <span className="ml-2 hidden sm:inline">Esporta</span>
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="panoramica" className="gap-2">
            <BarChart3 className="h-4 w-4" />
            <span className="hidden sm:inline">Panoramica</span>
          </TabsTrigger>
          <TabsTrigger value="prodotti" className="gap-2">
            <Package className="h-4 w-4" />
            <span className="hidden sm:inline">Prodotti</span>
          </TabsTrigger>
          <TabsTrigger value="clienti" className="gap-2">
            <Users className="h-4 w-4" />
            <span className="hidden sm:inline">Clienti</span>
          </TabsTrigger>
        </TabsList>

        {/* TAB PANORAMICA */}
        <TabsContent value="panoramica" className="space-y-6 mt-6">
          {/* KPI Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Fatturato</CardTitle>
                <DollarSign className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{formatCurrency(summary?.total_revenue || 0)}</div>
                <p className="text-xs text-muted-foreground">nel periodo selezionato</p>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Ordini</CardTitle>
                <ShoppingCart className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{formatNumber(summary?.total_orders || 0)}</div>
                <p className="text-xs text-muted-foreground">ordini ricevuti</p>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Valore Medio</CardTitle>
                <TrendingUp className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{formatCurrency(summary?.avg_order_value || 0)}</div>
                <p className="text-xs text-muted-foreground">per ordine</p>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Media Giornaliera</CardTitle>
                <Calendar className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{formatCurrency(summary?.avg_daily_revenue || 0)}</div>
                <p className="text-xs text-muted-foreground">fatturato/giorno</p>
              </CardContent>
            </Card>
          </div>

          {/* Grafici */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Trend Vendite */}
            <Card>
              <CardHeader>
                <CardTitle>Andamento Vendite</CardTitle>
                <CardDescription>Fatturato giornaliero nel periodo</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={salesTrend}>
                      <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                      <XAxis 
                        dataKey="date" 
                        tickFormatter={formatDate}
                        className="text-xs"
                      />
                      <YAxis tickFormatter={(v) => `€${v}`} className="text-xs" />
                      <Tooltip 
                        formatter={(value: number) => [formatCurrency(value), 'Fatturato']}
                        labelFormatter={(label) => new Date(label).toLocaleDateString('it-IT')}
                      />
                      <Area 
                        type="monotone" 
                        dataKey="revenue" 
                        stroke="#16a34a" 
                        fill="#16a34a" 
                        fillOpacity={0.2}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Distribuzione per Stato */}
            <Card>
              <CardHeader>
                <CardTitle>Ordini per Stato</CardTitle>
                <CardDescription>Distribuzione attuale degli ordini</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[300px]">
                  {ordersByStatus.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={ordersByStatus}
                          cx="50%"
                          cy="50%"
                          innerRadius={60}
                          outerRadius={100}
                          paddingAngle={2}
                          dataKey="count"
                          nameKey="status"
                          label={({ name, percent }: any) => `${getStatusLabel(name)} ${(percent * 100)?.toFixed(0)}%`}
                        >
                          {ordersByStatus.map((entry, index) => (
                            <Cell 
                              key={`cell-${index}`} 
                              fill={STATUS_COLORS[entry.status] || COLORS[index % COLORS.length]} 
                            />
                          ))}
                        </Pie>
                        <Tooltip formatter={(value: number, name: string) => [value, getStatusLabel(name)]} />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="flex items-center justify-center h-full text-muted-foreground">
                      Nessun dato disponibile
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Fatturato Mensile */}
          <Card>
            <CardHeader>
              <CardTitle>Fatturato Mensile</CardTitle>
              <CardDescription>Confronto degli ultimi 6 mesi</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={monthlyRevenue}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                    <XAxis dataKey="month_label" />
                    <YAxis tickFormatter={(v) => `€${v}`} />
                    <Tooltip formatter={(value: number) => [formatCurrency(value), 'Fatturato']} />
                    <Bar dataKey="revenue" fill="#2563eb" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* TAB PRODOTTI */}
        <TabsContent value="prodotti" className="space-y-6 mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Top Prodotti</CardTitle>
              <CardDescription>I prodotti più venduti nel periodo</CardDescription>
            </CardHeader>
            <CardContent>
              {topProducts.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>#</TableHead>
                      <TableHead>Prodotto</TableHead>
                      <TableHead className="text-right">Quantità</TableHead>
                      <TableHead className="text-right">Fatturato</TableHead>
                      <TableHead className="text-right">Ordini</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {topProducts.map((product, index) => (
                      <TableRow key={product.product_id}>
                        <TableCell>
                          <span className="w-6 h-6 rounded-full bg-primary/10 text-primary text-xs flex items-center justify-center font-bold">
                            {index + 1}
                          </span>
                        </TableCell>
                        <TableCell className="font-medium">{product.name}</TableCell>
                        <TableCell className="text-right">{formatNumber(product.quantity)}</TableCell>
                        <TableCell className="text-right">{formatCurrency(product.revenue)}</TableCell>
                        <TableCell className="text-right">{product.orders_count}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <p className="text-center text-muted-foreground py-8">Nessun dato disponibile</p>
              )}
            </CardContent>
          </Card>

          {/* Grafico Top Prodotti */}
          {topProducts.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Distribuzione Fatturato per Prodotto</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={topProducts.slice(0, 5)} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                      <XAxis type="number" tickFormatter={(v) => `€${v}`} />
                      <YAxis dataKey="name" type="category" width={150} className="text-xs" />
                      <Tooltip formatter={(value: number) => [formatCurrency(value), 'Fatturato']} />
                      <Bar dataKey="revenue" fill="#7c3aed" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* TAB CLIENTI */}
        <TabsContent value="clienti" className="space-y-6 mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Top Clienti</CardTitle>
              <CardDescription>I clienti con maggiore volume d'acquisto</CardDescription>
            </CardHeader>
            <CardContent>
              {topCustomers.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>#</TableHead>
                      <TableHead>Cliente</TableHead>
                      <TableHead className="text-right">Ordini</TableHead>
                      <TableHead className="text-right">Totale Speso</TableHead>
                      <TableHead className="text-right">Media Ordine</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {topCustomers.map((customer, index) => (
                      <TableRow key={customer.customer_id}>
                        <TableCell>
                          <span className="w-6 h-6 rounded-full bg-primary/10 text-primary text-xs flex items-center justify-center font-bold">
                            {index + 1}
                          </span>
                        </TableCell>
                        <TableCell className="font-medium">{customer.name}</TableCell>
                        <TableCell className="text-right">{customer.orders_count}</TableCell>
                        <TableCell className="text-right">{formatCurrency(customer.total_spent)}</TableCell>
                        <TableCell className="text-right">{formatCurrency(customer.avg_order_value)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <p className="text-center text-muted-foreground py-8">Nessun dato disponibile</p>
              )}
            </CardContent>
          </Card>

          {/* Grafico Top Clienti */}
          {topCustomers.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Distribuzione Fatturato per Cliente</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={topCustomers.slice(0, 5) as any}
                        cx="50%"
                        cy="50%"
                        outerRadius={100}
                        dataKey="total_spent"
                        nameKey="name"
                        label={({ name, percent }: any) => `${name?.slice(0, 15)}... ${(percent * 100).toFixed(0)}%`}
                      >
                        {topCustomers.slice(0, 5).map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value: number) => [formatCurrency(value), 'Fatturato']} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default SellerStatsPage;
